<template>
  <div class="fts-table-simple">
    <draggable
      v-model="formData[item.fieldName]"
      handle=".table-simple-row-action--handle"
    >
      <fts-collapsible-element
        v-for="(row, rowIndex) in formData[item.fieldName]"
        v-bind="dragOptions"
        :key="row.id"
        initial-visible
        class="table-simple-row"
        :class="{'table-simple-row--error': rowValidateError(rowIndex)}"
        :ref="`table-simple-row-${rowIndex + 1}`"
      >
        <div 
          slot="toggle"
          slot-scope="{ toggle, isVisible }"
          class="table-simple-row-header"
        >
          <div class="table-simple-row-actions" v-if="isControlEnabled">
            <font-awesome-icon 
              :icon="['fas', 'grip-vertical']" 
              fixed-width 
              class="table-simple-row-action table-simple-row-action--handle"
            />

            <font-awesome-icon 
              :icon="['fas', 'plus']" 
              fixed-width 
              class="table-simple-row-action table-simple-row-action--add" 
              @click="addRow(rowIndex + 1)"
            />

            <font-awesome-icon 
              :icon="['fas', 'trash']" 
              fixed-width 
              class="table-simple-row-action table-simple-row-action--remove" 
              @click="removeRow(row)"
            />
          </div>

          <div 
            class="table-simple-row-toggle" 
            :class="{'table-simple-row-toggle--collapse': !isVisible}" 
            @click="toggle"
          >
            <div>Item {{rowIndex + 1}}</div>
            <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width class="table-simple-row-toggle-icon" />
          </div>
        </div>

        <div slot="content" class="table-simple-row-content">
          <fts-form-element
            v-for="field in fields"
            :key="field.id"
            :vector-id="vectorId"
            :item="field"
            :form-data="row"
            :validator="rowValidator(rowIndex)"
            :comfort-design="comfortDesign"
            class="table-simple-row-element"
          />
        </div>
      </fts-collapsible-element>
    </draggable>

    <div 
      class="table-simple-add-btn" 
      @click="addRow(formData[item.fieldName] ? formData[item.fieldName].length : 0)" 
      v-if="isControlEnabled"
    >
      <font-awesome-icon :icon="['fas', 'plus']" fixed-width /> add new
    </div>
  </div>
</template>

<script>
import commons from '../commons/commons';

import draggable from 'vuedraggable';
import FTSBtn from './FTSBtn';
import FTSCollapsibleElement from './FTSCollapsibleElement';

export default {
  components: {
    draggable,
    "fts-btn": FTSBtn,
    "fts-collapsible-element": FTSCollapsibleElement
  },
  props: {
    vectorId: [Number, String],
    item: Object,
    formData: Object,
    validator: Object,
    comfortDesign: Boolean
  },
  data: () => ({
    dragOptions: commons.dragOptions
  }),
  methods: {
    addRow(index) {
      let newRow = { id: commons.newUUID() };
      this.item.fields.forEach(x => {
        if (x && x.fieldName.indexOf(".") === -1) {
          this.$set(newRow, x.fieldName, null);
        }
      });

      if (this.formData[this.item.fieldName] == null) {
        this.$set(this.formData, this.item.fieldName, []);
      }

      this.formData[this.item.fieldName].splice(index, 0, newRow);
      this.$nextTick(() => {
        try {
          this.$refs[`table-simple-row-${index + 1}`][0].$el.scrollIntoView();
        } catch(err) {
          console.warn(err);
        }
      });
    },
    removeRow(row) {
      let tableData = this.formData[this.item.fieldName];
      tableData.splice(tableData.indexOf(row), 1);
    },
    rowValidator(index) {
      try {
        const validations =  Object.values(this.validator.$each.$iter);
        return validations[index];
      } catch(err) {
        console.warn(err);
      }
    },
    rowValidateError(index) {
      const rowValidator = this.rowValidator(index);
      return rowValidator && rowValidator.$error ? true : false;
    }
  },
  computed: {
    isControlEnabled() {
      // hideTableActions (legacy)
      return !Boolean(this.item && (this.item.hideTableActions || this.item.disabledAddRemoveItems)); 
    },
    fields() {
      let fields = this.item.fields;
      // columns (legacy)
      if (!this.item.fields && this.item.columns) {
        fields = this.item.columns;
      }

      fields = fields.filter(x => x.input != "hidden" && !x.hide && x.visible !== false);
      return fields;
    }
  },
  beforeCreate() {
    this.$options.components.FtsFormElement = require("./FTSFormElement").default;
  }
}
</script>

<style lang="scss" scoped>
.fts-table-simple {
  .table-simple-row {
    margin-bottom: 0.75rem;
    border: 1px solid var(--fts-form-control-border);
    border-radius: 0.25rem;
    background-color: var(--fts-card-background);
    scroll-margin-top: 1rem;
    &.table-simple-row--error {
      border-color: var(--fts-danger);
    }
  }
  .table-simple-row-header {
    display: flex;
    align-items: center;
    color: var(--fts-form-label);
  }
  .table-simple-row-actions {
    padding: 0.75rem 0 0.75rem 0.75rem;
    font-size: 0.75rem;
  }
  .table-simple-row-action {
    cursor: pointer;
    &.table-simple-row-action--handle {
      cursor: grab;
    }
    &:hover {
      &.table-simple-row-action--add {
        color: var(--fts-primary);
      }
      &.table-simple-row-action--remove {
        color: var(--fts-danger);
      }
    }
  }
  .table-simple-row-toggle {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem;
    font-size: 0.875rem;
    cursor: pointer;
    user-select: none;
    .table-simple-row-toggle-icon {
      transition: 0.3s ease-in-out;
    }
    &.table-simple-row-toggle--collapse {
      .table-simple-row-toggle-icon {
        transform: rotate(-90deg);
      }
    }
  }
  .table-simple-row-content {
    display: flex;
    flex-wrap: wrap;
    padding: 0.25rem 0.75rem 0.75rem;
  }
  .table-simple-add-btn {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem;
    border-radius: 0.25rem;
    color: var(--fts-form-label);
    font-size: 0.875rem;
    line-height: 1.5;
    user-select: none;
    cursor: pointer;
    > svg {
      margin-right: 0.375rem;
    }
    &:hover {
      color: var(--fts-primary);
      background-color: rgba(var(--fts-gray-100-rgb), 0.75);
    }
  }
}
</style>